import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { FinetuneRunService } from "../../../../services/FinetuneRunService";
import { TrainingMethod } from "../../../../model_gen/finetuning";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { SettingsIcon } from "lucide-react";
import ReviewField from "./ReviewField";
import PythonCodeBlock from "../../../shared/PythonCodeBlock";

interface ReviewFinetuneSettingsCardProps {
  trainingMethod?: TrainingMethod;
  dpoBeta?: number;
  grpoNumGenerations?: number;
  grpoMaxCompletionLength?: number;
  grpoRewardFunction?: string;
  displayName: string;
  description?: string;
  baseModel: string;
  baseFinetuneRunId?: number | null;
  fourbit: boolean;
  warmupRatio: number;
  learningRate: number;
  perDeviceTrainBatchSize: number;
  gradientAccumulationSteps: number;
  trainOnResponsesOnly: boolean;
  lrSchedulerType?: string;
  trainSplit: number;
  alpha?: number;
  rank?: number;
  epochs?: number;
  weightDecay?: number;
  dropout?: number;
  rslora?: boolean;
  numGpus?: number;
}

const ReviewFinetuneSettingsCard: React.FC<ReviewFinetuneSettingsCardProps> = ({
  trainingMethod = "SFT",
  dpoBeta,
  grpoNumGenerations,
  grpoMaxCompletionLength,
  grpoRewardFunction,
  displayName,
  description,
  baseModel,
  baseFinetuneRunId,
  fourbit,
  warmupRatio,
  learningRate,
  perDeviceTrainBatchSize,
  gradientAccumulationSteps,
  trainOnResponsesOnly,
  lrSchedulerType,
  trainSplit,
  alpha,
  rank,
  epochs,
  weightDecay,
  dropout,
  rslora,
  numGpus = 1,
}) => {
  const { apiKey } = usePermissions();
  const [baseFinetunedModelName, setBaseFinetunedModelName] = useState<string>("");
  const [baseFinetunedModelLoading, setBaseFinetunedModelLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!baseFinetuneRunId) {
      setBaseFinetunedModelName("");
      return;
    }
    setBaseFinetunedModelLoading(true);
    const sub = FinetuneRunService.getFinetuneRunOptions(
      0,
      1,
      setBaseFinetunedModelLoading,
      apiKey,
      undefined,
      undefined,
      undefined,
      undefined,
      baseFinetuneRunId
    ).subscribe(
      (resp: any) => {
        const run = resp?.items?.[0];
        setBaseFinetunedModelName(run?.display_name || "");
      },
      (error: Error) => {
        console.error("Error fetching base finetune run details:", error);
        setBaseFinetunedModelName("");
        setBaseFinetunedModelLoading(false);
      }
    );
    return () => sub.unsubscribe();
  }, [baseFinetuneRunId, apiKey]);

  const resolvedBaseModel = baseFinetuneRunId
    ? baseFinetunedModelLoading
      ? "Loading..."
      : baseFinetunedModelName || "Unknown"
    : baseModel;

  const lrSchedulerLabels: Record<string, string> = {
    linear: "Linear Decay",
    cosine: "Cosine Annealing",
    cosine_with_restarts: "Cosine with Warm Restarts",
    polynomial: "Polynomial Decay",
    constant: "Constant",
    inverse_sqrt: "Inverse Square Root",
  };

  const optionalFields: Array<{ label: string; value: React.ReactNode; show: boolean }> = [
    { label: "Epochs", value: epochs, show: epochs !== undefined },
    { label: "Weight Decay", value: weightDecay, show: weightDecay !== undefined },
    { label: "Dropout", value: dropout, show: dropout !== undefined },
    { label: "Rank", value: rank, show: rank !== undefined },
    { label: "Alpha", value: alpha, show: alpha !== undefined },
    { label: "RSLoRA", value: rslora ? "Yes" : "No", show: rslora !== undefined },
  ];

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
          <SettingsIcon className="w-5 h-5 text-maitai-green" />
          Finetune Settings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ReviewField label="Training Method" value={trainingMethod} />
          <ReviewField label="Display Name" value={displayName || "Not specified"} />
          <ReviewField label="Base Model" value={resolvedBaseModel} />
          <ReviewField label="4-bit Quantization" value={fourbit ? "Yes" : "No"} />
          <ReviewField label="GPUs" value={numGpus > 1 ? `${numGpus} (distributed)` : `${numGpus}`} />
          {trainingMethod === "DPO" && dpoBeta !== undefined && (
            <ReviewField label="DPO Beta" value={dpoBeta} />
          )}
          {trainingMethod === "GRPO" && grpoNumGenerations !== undefined && (
            <ReviewField label="Num Generations" value={grpoNumGenerations} />
          )}
          {trainingMethod === "GRPO" && grpoMaxCompletionLength !== undefined && (
            <ReviewField label="Max Completion Length" value={grpoMaxCompletionLength} />
          )}
          <ReviewField label="Warmup Ratio" value={warmupRatio} />
          <ReviewField label="Learning Rate" value={learningRate} />
          <ReviewField label="LR Schedule" value={lrSchedulerLabels[lrSchedulerType || "linear"] || lrSchedulerType} />
          <ReviewField label="Batch Size" value={perDeviceTrainBatchSize} />
          <ReviewField label="Gradient Accumulation" value={gradientAccumulationSteps} />
          {trainingMethod === "SFT" && (
            <ReviewField label="Train on Responses Only" value={trainOnResponsesOnly ? "Yes" : "No"} />
          )}
          <ReviewField label="Train Split" value={`${(trainSplit * 100).toFixed(0)}%`} />
          {optionalFields
            .filter((f) => f.show)
            .map((f) => (
              <ReviewField key={f.label} label={f.label} value={f.value} />
            ))}
        </div>

        {trainingMethod === "GRPO" && grpoRewardFunction && (
          <div className="mt-6 pt-6 border-t border-border space-y-2">
            <ReviewField label="Reward Function" value="Custom" />
            <PythonCodeBlock code={grpoRewardFunction} />
          </div>
        )}

        {description && (
          <div className="mt-6 pt-6 border-t border-border">
            <ReviewField label="Description" value={description} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewFinetuneSettingsCard;
